import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { Observable, filter, mergeMap, withLatestFrom } from 'rxjs';
import { SpxStorage, SpxStorageKeyEnum } from '@softpak/components/spx-storage';

import { AmqMoveActions } from '../amq-move/amq-move.actions';
import { AmqMoveTypeEnum } from 'src/app/shared/models/shared/shared-rtg.interface';
import { AmqSocketActions } from './amq-socket.actions';
import { OrdSubStatusEnum } from '../ord-search/models/ord-sub-status.enum';
import { OrdTransactionTypeEnum } from 'src/app/api/ord-process/models/ord-transaction-type.enum';
import { SharedPageTypeEnum } from 'src/app/shared/navigation/page-type.enum';
import { Store } from '@ngrx/store';
import amqModeReducer from '../amq-mode/amq-mode.reducer';
import ordSearchReducer from '../ord-search/ord-search.reducer';
import { ordDetailsReducer } from '../ord-details';
import sharedContainerSingleReducer from '../shared-container-single/shared-container-single.reducer';
import { statsActionActions } from '../stats-action';
import { valuePairToValue } from '@softpak/components/spx-helpers';

@Injectable()
export class AmqSocketEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);

    trackSocketConnection$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(AmqSocketActions.log),
        filter(action => action.logType === 'connect' || action.logType === 'disconnect'),
        mergeMap(action => [
            this.createStatsAction(action.logType === 'connect' ? 'AMQ_SOCKET_CONNECTED' : 'AMQ_SOCKET_DISCONNECTED'),
        ])
    ));

    startMove$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(AmqSocketActions.received),
        filter((action) => {
            return action.result.event === AmqMoveTypeEnum.lock;
        }),
        withLatestFrom(
            this.store.select(amqModeReducer.selectAmqModeState),
            this.store.select(ordSearchReducer.selectData),
            this.store.select(ordDetailsReducer.default.selectData),
        ),
        mergeMap(([action, amqMode, ordersData, currentOrder]) => {
            const scannedLocation = action.result.location;
            const containerSequenceNumber = currentOrder?.seqNr ?? null;
            const orderSequenceNumber = currentOrder?.orderSequenceNumber ?? null;

            if (scannedLocation) {
                const orders = ordersData?.result?.flatMap(g => g.orders ?? []) ?? [];
                const pickUps = orders.filter(order => order.transactionType === OrdTransactionTypeEnum.pickUp);
                const sqRelocates = orders.filter(order => order.transactionType === OrdTransactionTypeEnum.relocate && order.subStatus === OrdSubStatusEnum.relocateStackToQuay);
                const allRelocateOrders = orders.filter(order => order.transactionType === OrdTransactionTypeEnum.relocate);

                const matchesPickups = pickUps.find(order => scannedLocation && scannedLocation === order.locationOrigin);
                const matchedSqRelocate = sqRelocates.find(order => scannedLocation && scannedLocation === order.locationOrigin);
                const matchedRelocate = allRelocateOrders.find(order => scannedLocation && scannedLocation === order.locationOrigin);

                if (matchesPickups) {
                    return [
                        AmqMoveActions.askPickUpOrRelocate({ pickUpOrder: matchesPickups, relocateOrder: matchedRelocate }),
                    ];
                }
                if (matchedSqRelocate) {
                    return [
                        AmqMoveActions.askSqRelocate({ order: matchedSqRelocate }),
                    ];
                }
                if (!matchedSqRelocate && matchedRelocate) {
                    return [
                        AmqMoveActions.askRelocate({ order: matchedRelocate }),
                    ];
                }
                return [
                    AmqMoveActions.startStandardRelocate({ location: action.result.location }),
                ];
            } else {
                if (scannedLocation === null) {
                    if (amqMode.onStartDropOffPage) {
                        return [
                            AmqMoveActions.startDropOff({
                                containerSequenceNumber,
                                orderSequenceNumber,
                            }),
                        ]
                    }
                    if (amqMode.onRelocateOrderPage) {
                        return [
                            AmqMoveActions.startRelocateFromEmptyLocation({
                                containerSequenceNumber,
                                orderSequenceNumber,
                            }),
                        ]
                    }
                }
            }
            return [];
        }))
    );

    endMove$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(AmqSocketActions.received),
        filter((action) => {
            return action.result.event === AmqMoveTypeEnum.unlock;
        }),
        withLatestFrom(
            this.store.select(amqModeReducer.selectAmqModeState),
            this.store.select(ordDetailsReducer.default.selectData),
            this.store.select(sharedContainerSingleReducer.selectDataForPageType(SharedPageTypeEnum.ordAutomaticShuffle)),
        ),
        mergeMap(([action, amqMode, currentOrder, currentContainer]) => {
            const containerSequenceNumber = currentOrder?.seqNr ?? null;
            const orderSequenceNumber = currentOrder?.orderSequenceNumber ?? null;
            const standardRelocateContainerSequenceNumber = currentContainer?.seqNr != null
                ? valuePairToValue(currentContainer.seqNr)
                : null;

            if (action.result.location) {
                if (amqMode.onFinishDropOffPage) {
                    return [
                        AmqMoveActions.finishDropOff({
                            location: action.result.location,
                            containerSequenceNumber,
                            orderSequenceNumber,
                        }),
                    ]
                }
                if (amqMode.onRelocateOrderPage) {
                    return [
                        AmqMoveActions.finishRelocateOrder({
                            location: action.result.location,
                            containerSequenceNumber,
                            orderSequenceNumber,
                        }),
                    ]
                }
                if (amqMode.onStandardRelocatePage) {
                    return [
                        AmqMoveActions.finishStandardRelocate({
                            location: action.result.location,
                            containerSequenceNumber: standardRelocateContainerSequenceNumber ?? null,
                        }),
                    ]
                }
            } else {
                if (amqMode.onPickUpPage) {
                    return [
                        AmqMoveActions.finishPickup({
                            containerSequenceNumber,
                            orderSequenceNumber,
                        }),
                    ];
                }
            }
            return [];
        }))
    );

    private createStatsAction(action: string) {
        return statsActionActions.load({
            query: {
                app: 'orders',
                customer: SpxStorage.getSetting(SpxStorageKeyEnum.brand),
                action,
                randomDeviceId: SpxStorage.getSetting(SpxStorageKeyEnum.randomDeviceId),
                username: SpxStorage.getSetting(SpxStorageKeyEnum.username),
            },
        });
    }
}
